//src/components/TaskReminderList.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchTaskReminderList, markTaskReminderComplete, updateTaskReminder, deleteTaskReminder } from '../../utils/api';
import { TaskReminder } from '../../utils/types';


const PRODUCTS_PER_PAGE_MODAL = 10;

// Helper to format date string for datetime-local input
const formatDateTimeForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    // This formats the date to 'YYYY-MM-DDTHH:mm' which is required by the input
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const TaskReminderList: React.FC = () => {

    const { t } = useTranslation();

    const [taskReminders, setTaskReminders] = useState<TaskReminder[]>([]); // Replace 'any' with appropriate type
    const [activeTab, setActiveTab] = useState<'new' | 'completed' | 'tasks'>('tasks');
    const [key, setKey] = useState(0); // Used to force re-render of TaskReminderList

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [completedIds, setCompletedIds] = useState<Record<number, boolean>>({}); // <-- added

    // State for inline editing
    const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
    const [editedTask, setEditedTask] = useState<{ title: string; description: string; reminder_at: string } | null>(null);


    // Fetch TaskReminder items function (to be implemented)
    const fetchTaskReminders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response: any = await fetchTaskReminderList();

            setTaskReminders(response.task_reminders);

            // initialize completed state if API returns completed flag
            const init = Object.fromEntries(
                (response.task_reminders || []).map((it: TaskReminder) => [it.id, !!(it as any).is_completed])
            );
            setCompletedIds(init);

        } catch (err) {
            console.error('Error fetching task reminders:', err);
            setError(t('taskReminder.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchTaskReminders();
    }, [fetchTaskReminders]);

    const toggleComplete = (id: number) => {
        setCompletedIds(prev => {
            const newCompletedStatus = !prev[id];
            const next = { ...prev, [id]: newCompletedStatus };

            // Optimistically update the UI, but prepare to rollback on failure.
            const updateStatus = async () => {
                try {
                    await markTaskReminderComplete(id, newCompletedStatus);
                    toast.success(t('taskReminder.updateSuccess') as string);
                } catch (error) {
                    toast.error(t('taskReminder.failedToUpdateCompletion', { error }) as string);
                    // Rollback on error
                    setCompletedIds(prevRollback => ({ ...prevRollback, [id]: prev[id] }));
                }
            };
            updateStatus();

            return next;
        });
    };

    const handleDeleteClick = async (task: TaskReminder) => {
        // if (window.confirm(t('taskReminder.confirmDelete') as string || 'Are you sure you want to delete this task?')) {
            setLoading(true);
            try {
                await deleteTaskReminder(task.id);
                toast.success(t('taskReminder.deleteSuccess') as string || 'Task deleted successfully');
                fetchTaskReminders();
            } catch (error) {
                console.error('Error deleting task:', error);
                toast.error(t('taskReminder.deleteError') as string || 'Failed to delete task');
            } finally {
                setLoading(false);
            }
        // }
    };

    const handleEditClick = (task: TaskReminder) => {
        setEditingTaskId(task.id);
        setEditedTask({
            title: task.title,
            description: task.description || '',
            reminder_at: formatDateTimeForInput(task.reminder_at),
        });
    };

    const handleCancelEdit = () => {
        setEditingTaskId(null);
        setEditedTask(null);
    };

    const handleSaveEdit = async (id: number) => {
        if (!editedTask) return;

        setLoading(true);
        try {

            await updateTaskReminder(id, {
                title: editedTask.title,
                description: editedTask.description,
                reminder_at: new Date(editedTask.reminder_at).toISOString(),
            });

            toast.success('Task updated successfully!');
            setEditingTaskId(null);
            setEditedTask(null);
            fetchTaskReminders(); // Refresh list

        } catch (error) {
            toast.error('Failed to update task.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <p>{t('taskReminder.loading')}</p>;
    }

    return (
        <div className="task-reminder-list bg-gray-50 m-2 p-4 pr-8 pb-16 rounded-lg shadow-md">
            {/* <h2 className="text-lg font-bold mb-4">{t('taskReminder.taskReminderList')}</h2> */}
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                    onClick={() => setActiveTab('tasks')}
                    className={`${activeTab === 'tasks' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                    {t('taskReminder.upcoming')}
                </button>
                <button
                    onClick={() => setActiveTab('completed')}
                    className={`${activeTab === 'completed' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                    {t('taskReminder.completed')}
                </button>
            </nav>
            {activeTab === 'tasks' && (
                <div className="mt-4">
                    {loading && <p>{t('taskReminder.loading')}</p>}
                    {error && <p className="text-red-500">{error}</p>}
                    {!loading && !error && taskReminders.length === 0 && (
                        <p>{t('taskReminder.noTaskReminders')}</p>
                    )}

                    {!loading && !error && taskReminders.length > 0 && (
                        <ul>
                            {/* {taskReminders.map((item) => ( */}
                            {taskReminders.filter(item => !completedIds[item.id]).map((item) => (

                                <li key={item.id} className="border-b border-3 border-white my-1 px-4 py-2 rounded-lg bg-green-100">
                                    {editingTaskId === item.id ? (
                                        <div className="w-full space-y-2">
                                            <input
                                                type="text"
                                                value={editedTask?.title}
                                                onChange={(e) => setEditedTask(prev => prev ? { ...prev, title: e.target.value } : null)}
                                                className="w-full border p-1 rounded bg-white"
                                            />
                                            <textarea
                                                value={editedTask?.description}
                                                onChange={(e) => setEditedTask(prev => prev ? { ...prev, description: e.target.value } : null)}
                                                className="w-full border p-1 rounded bg-white"
                                            />
                                            <input
                                                type="datetime-local"
                                                value={editedTask?.reminder_at}
                                                onChange={(e) => setEditedTask(prev => prev ? { ...prev, reminder_at: e.target.value } : null)}
                                                className="w-full border p-1 rounded bg-white"
                                            />
                                            <div className="flex justify-end space-x-2 mt-2">
                                                <button onClick={handleCancelEdit} className="px-3 py-1 bg-gray-300 rounded">Cancel</button>
                                                <button onClick={() => handleSaveEdit(item.id)} className="px-3 py-1 bg-blue-600 text-white rounded">Save</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-end md:flex-row md:items-start md:justify-between">
                                            <label className="flex items-start space-x-3 w-full">
                                                <input
                                                    type="checkbox"
                                                    checked={!!completedIds[item.id]}
                                                    onChange={() => toggleComplete(item.id)}
                                                    className="mt-1 h-4 w-4"
                                                    aria-label={`Mark ${item.title} complete`}
                                                />

                                                <button onClick={() => handleEditClick(item)} className="flex flex-col text-left mr-4">
                                                    <span className="font-bold text-indigo-600">Edit</span>
                                                </button>

                                                <div className="flex flex-col">
                                                    <span className="font-bold">{item.title}</span>
                                                    <span className='pl-2 py-2 text-gray-500 '>{item.description}</span>
                                                </div>
                                            </label>
                                            <div className="w-48 text-xs ml-4 text-right">
                                                {(() => {
                                                    const dt = new Date(item.reminder_at);
                                                    if (isNaN(dt.getTime())) return `Due on ${item.reminder_at}`;
                                                    const isPastDue = dt < new Date();
                                                    const dateColorClass = isPastDue ? 'text-red-500 font-bold' : '';
                                                    const pad = (n: number) => n.toString().padStart(2, '0');
                                                    return <span className={dateColorClass}>Due on {`${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`}</span>;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </li>

                            ))}
                        </ul>
                    )}

                </div>
            )}

            {activeTab === 'completed' && (
                <div className="mt-4">
                    {loading && <p>{t('taskReminder.loading')}</p>}
                    {error && <p className="text-red-500">{error}</p>}
                    {!loading && !error && taskReminders.filter(item => completedIds[item.id]).length === 0 && (
                        <p>{t('taskReminder.noTaskReminders')}</p>
                    )}
                    {!loading && !error && taskReminders.filter(item => completedIds[item.id]).length > 0 && (
                        <ul>
                            {taskReminders.filter(item => completedIds[item.id]).map((item) => (
                                <li key={item.id} className="border-b border-3 border-white my-1 p-2 rounded-lg bg-gray-200">

                                    <div className="flex flex-col items-end md:flex-row md:items-start md:justify-between">
                                        <label className="flex items-start space-x-3 w-full">
                                            <input
                                                type="checkbox"
                                                checked={!!completedIds[item.id]}
                                                onChange={() => toggleComplete(item.id)}
                                                className="mt-1 h-4 w-4"
                                                aria-label={`Mark ${item.title} complete`}
                                            />
                                            <div className="flex flex-col">
                                                <span className={`font-bold ${completedIds[item.id] ? 'line-through text-gray-400' : ''}`}>
                                                    {item.title}
                                                </span>
                                                <span className={`${completedIds[item.id] ? 'line-through text-gray-400' : ''}`}>{item.description}</span>
                                            </div>
                                        </label>
                                        <div className={`w-56 text-xs ml-4 text-right ${completedIds[item.id] ? 'line-through text-gray-400' : ''}`}>
                                            {(() => {
                                                const dt = new Date(item.reminder_at);
                                                if (isNaN(dt.getTime())) return `Due on ${item.reminder_at}`;

                                                const pad = (n: number) => n.toString().padStart(2, '0');
                                                return <span>Due on {`${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`}</span>;
                                            })()}
                                        </div>

                                        <button onClick={() => handleDeleteClick(item)} className="flex flex-col text-left mx-4">
                                            <span className="text-xs font-bold text-red-600">Delete</span>
                                        </button>


                                    </div>
                                </li>

                            ))}
                        </ul>
                    )}
                </div>
            )}

        </div>
    );
};

export default TaskReminderList;